/**
 * Phase 66 Plan 06 Task 2 — Global Cmd+K command palette (D-09).
 *
 * Renders a shadcn `<CommandDialog>` with grouped fuzzy-search results
 * across 4 entity types: machines, users, provisioning queue, audit log.
 *
 * Lifecycle:
 *   1. Mounted at the same level as <RouterProvider /> in main.tsx.
 *   2. Returns null when there is no authenticated user (avoids the
 *      hotkey + navigation firing on the login screen).
 *   3. Global keydown listener: Cmd+K (Mac) / Ctrl+K (Linux/Win) toggles
 *      the dialog. e.preventDefault() before toggle.
 *
 * Linux Firefox quirk (RESEARCH §Pitfall 6): browsers historically map
 * Ctrl+K to "focus search bar". We e.preventDefault() unconditionally
 * before toggling — that beats the browser default on Chrome/Firefox/
 * Safari. The placeholder hint shows ⌘K on Mac, Ctrl+K elsewhere.
 *
 * Tenant-switch flicker (C-7): each list query passes `placeholderData:
 * keepPreviousData` so during the ~500ms refetch window after a tenant
 * switch (Task 1 calls queryClient.invalidateQueries()), the palette
 * keeps rendering the previous tenant's results. Backend re-validates
 * RBAC on every request — showing stale-but-tenant-scoped data is not
 * a security risk.
 *
 * Search:
 *   - 200ms debounce on the input value
 *   - Each group caps at 8 results to keep the dialog scannable
 *   - Empty input = no results (the cards already cap at 8 anyway)
 *
 * Cache reuse: `useMachines / useUsers / useProvisioningQueue` share
 * cache entries with the corresponding list pages, so opening the
 * palette costs nothing on a warm cache.
 */

import { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { keepPreviousData, useQuery } from "@tanstack/react-query";
import { ClipboardList, FileText, Server, User as UserIcon } from "lucide-react";

import {
  CommandDialog,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command";
import { useAuth } from "@/auth/AuthProvider";
import { useMachines } from "@/hooks/queries/useMachines";
import { useUsers } from "@/hooks/queries/useUsers";
import { useProvisioningQueue } from "@/hooks/queries/useProvisioningQueue";
import { useCircuitId } from "@/hooks/useCircuitId";
import { listAudit, type AuditEvent } from "@/api/audit";
import { qk } from "@/lib/queryKeys";
import { router } from "@/router";

function useDebounce<T>(value: T, delayMs: number): T {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const t = window.setTimeout(() => setDebounced(value), delayMs);
    return () => window.clearTimeout(t);
  }, [value, delayMs]);
  return debounced;
}

function isMacPlatform(): boolean {
  if (typeof navigator === "undefined") return false;
  return /Mac|iPhone|iPad|iPod/i.test(navigator.platform || "");
}

function matches(haystack: string | null | undefined, needle: string): boolean {
  if (!haystack) return false;
  if (!needle) return true;
  return haystack.toLowerCase().includes(needle.toLowerCase());
}

export default function CommandPalette() {
  const { t } = useTranslation();
  const { user } = useAuth();
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const debouncedQuery = useDebounce(query, 200);
  const isMac = useMemo(isMacPlatform, []);

  // Reuse cached lists (warm cache, no extra fetches when palette opens).
  // C-7: keepPreviousData smooths the tenant-switch refetch window.
  const { data: machines = [] } = useMachines({
    placeholderData: keepPreviousData,
    enabled: !!user,
  });
  const { data: usersList = [] } = useUsers({
    placeholderData: keepPreviousData,
    enabled: !!user,
  });
  const { data: queue = [] } = useProvisioningQueue({
    placeholderData: keepPreviousData,
    enabled: !!user,
  });

  // Audit is fetched lazily — only when the palette is open and the user
  // has audit:read scope. Tenant-scoped via qk.audit.
  const circuitId = useCircuitId();
  const tenant = circuitId ?? "all";
  const canReadAudit = user?.role_scopes?.includes("audit:read") ?? false;
  const { data: auditPage } = useQuery({
    queryKey: qk.audit(tenant, { palette: true }),
    queryFn: () => listAudit({}, null, 20),
    enabled: open && canReadAudit && !!user,
    staleTime: 30_000,
    placeholderData: keepPreviousData,
  });
  const auditEvents: AuditEvent[] = auditPage?.items ?? [];

  // Global hotkey: Cmd+K on Mac, Ctrl+K elsewhere.
  useEffect(() => {
    if (!user) return;
    const handler = (e: KeyboardEvent) => {
      if (e.key === "k" && (e.metaKey || e.ctrlKey)) {
        e.preventDefault();
        setOpen((o) => !o);
      }
    };
    document.addEventListener("keydown", handler);
    return () => document.removeEventListener("keydown", handler);
  }, [user]);

  const go = useCallback((path: string) => {
    setOpen(false);
    setQuery("");
    // Imperative navigation via the router instance — works because
    // <CommandPalette /> is mounted as a SIBLING of <RouterProvider />
    // in main.tsx (so the useNavigate hook context is unavailable here).
    void router.navigate(path);
  }, []);

  if (!user) return null;

  const filteredMachines = machines
    .filter(
      (m) =>
        matches(m.cpu_id, debouncedQuery) ||
        matches(m.name, debouncedQuery) ||
        matches(m.wireguard_ip, debouncedQuery),
    )
    .slice(0, 8);

  const filteredUsers = usersList
    .filter(
      (u) =>
        matches(u.user_code, debouncedQuery) ||
        matches(u.circuit_id, debouncedQuery),
    )
    .slice(0, 8);

  const filteredQueue = queue
    .filter((q) => matches(q.cpu_id, debouncedQuery) || matches(q.status, debouncedQuery))
    .slice(0, 8);

  const filteredAudit = auditEvents
    .filter(
      (a) =>
        matches(a.operation, debouncedQuery) ||
        matches(a.source, debouncedQuery) ||
        matches(a.admin_email, debouncedQuery) ||
        matches(a.target_path, debouncedQuery),
    )
    .slice(0, 8);

  const placeholder = t("command.placeholder", {
    shortcut: isMac ? "⌘K" : "Ctrl+K",
  });

  return (
    <CommandDialog
      open={open}
      onOpenChange={setOpen}
      title={t("command.placeholder", { shortcut: isMac ? "⌘K" : "Ctrl+K" })}
      description={t("command.empty")}
    >
      <div data-testid="command-palette">
        <CommandInput
          placeholder={placeholder}
          value={query}
          onValueChange={setQuery}
        />
        <CommandList>
          <CommandEmpty>{t("command.empty")}</CommandEmpty>
          {filteredMachines.length > 0 && (
            <CommandGroup heading={t("command.groups.machines")}>
              {filteredMachines.map((m) => (
                <CommandItem
                  key={`machine-${m.cpu_id}`}
                  data-testid={`command-item-machine-${m.cpu_id}`}
                  value={`machine ${m.cpu_id} ${m.name} ${m.wireguard_ip}`}
                  onSelect={() => go(`/machines/${encodeURIComponent(m.cpu_id)}`)}
                >
                  <Server className="mr-2" aria-hidden="true" />
                  <span className="truncate">{m.name || m.cpu_id}</span>
                  <span className="ml-auto text-xs text-[color:var(--color-text-muted)] tabular-nums">
                    {m.cpu_id}
                  </span>
                </CommandItem>
              ))}
            </CommandGroup>
          )}
          {filteredUsers.length > 0 && (
            <CommandGroup heading={t("command.groups.users")}>
              {filteredUsers.map((u) => {
                const key = `${u.circuit_id}:${u.user_code}`;
                return (
                  <CommandItem
                    key={`user-${key}`}
                    data-testid={`command-item-user-${u.user_code}`}
                    value={`user ${u.user_code} ${u.circuit_id}`}
                    onSelect={() =>
                      go(`/users?code=${encodeURIComponent(u.user_code)}`)
                    }
                  >
                    <UserIcon className="mr-2" aria-hidden="true" />
                    <span className="truncate">{u.user_code}</span>
                    <span className="ml-auto text-xs text-[color:var(--color-text-muted)] tabular-nums">
                      {u.balance}
                    </span>
                  </CommandItem>
                );
              })}
            </CommandGroup>
          )}
          {filteredQueue.length > 0 && (
            <CommandGroup heading={t("command.groups.provisioning")}>
              {filteredQueue.map((q) => (
                <CommandItem
                  key={`prov-${q.id}`}
                  data-testid={`command-item-prov-${q.id}`}
                  value={`provisioning ${q.cpu_id} ${q.status}`}
                  onSelect={() => go(`/provisioning`)}
                >
                  <ClipboardList className="mr-2" aria-hidden="true" />
                  <span className="truncate">{q.cpu_id}</span>
                  <span className="ml-auto text-xs text-[color:var(--color-text-muted)]">
                    {q.status}
                  </span>
                </CommandItem>
              ))}
            </CommandGroup>
          )}
          {canReadAudit && filteredAudit.length > 0 && (
            <CommandGroup heading={t("command.groups.audit")}>
              {filteredAudit.map((a, idx) => (
                <CommandItem
                  key={`audit-${a.correlation_id ?? idx}-${a.ts}`}
                  data-testid={`command-item-audit-${idx}`}
                  value={`audit ${a.operation} ${a.source} ${a.admin_email} ${a.target_path ?? ""}`}
                  onSelect={() => {
                    const cid = a.correlation_id;
                    go(cid ? `/admin/audit?correlation_id=${encodeURIComponent(cid)}` : `/admin/audit`);
                  }}
                >
                  <FileText className="mr-2" aria-hidden="true" />
                  <span className="truncate">{a.operation}</span>
                  <span className="ml-auto text-xs text-[color:var(--color-text-muted)]">
                    {a.source}
                  </span>
                </CommandItem>
              ))}
            </CommandGroup>
          )}
        </CommandList>
      </div>
    </CommandDialog>
  );
}
